import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { useGetAssociatedParty } from '@/shared/hooks/api/associatedParties'
import { useGetProduct } from '@/shared/hooks/api/product'
import {
  useGetCatalogComponent,
  useGetCatalogSupplier,
} from '@/shared/hooks/api/catalog'
import {
  V2_ORGS_ROLES_PATH,
  V2_COMPONENTS_PATH,
  V2_PRODUCTS_PATH,
  V2_SETTINGS_PATH,
  V2_SUPPLIERS_PATH,
  getV2OrgsRolesDetailId,
  getV2ProductDetailPath,
  getV2ProductLawFromPath,
  isV2ProductPartsPath,
} from './utils/routes'

type BreadcrumbLinkItem = {
  kind: 'link'
  label: string
  to: string
}

type BreadcrumbPageItem = {
  kind: 'page'
  label: string
}

type BreadcrumbItemConfig = BreadcrumbLinkItem | BreadcrumbPageItem

const SETTINGS_SECTION_LABEL_KEYS: Record<string, string> = {
  account: 'settings.account',
  organization: 'settings.organization',
  llm: 'settings.llm',
  theme: 'settings.theme',
  language: 'settings.languageOptions',
}

function getSettingsSection(pathname: string): string {
  const suffix = pathname.slice(V2_SETTINGS_PATH.length).replace(/^\//, '')
  const section = suffix.split('/')[0]
  if (section === '') {
    return 'account'
  }
  return section
}

function useV2BreadcrumbItems(): {
  items: BreadcrumbItemConfig[]
  isLoading: boolean
} {
  const { t } = useTranslation('common')
  const { pathname } = useLocation()
  const { productId, componentId, supplierId } = useParams<{
    productId?: string
    componentId?: string
    supplierId?: string
  }>()
  const associatedPartyId = getV2OrgsRolesDetailId(pathname)
  const isProductDetailRoute =
    productId != null &&
    productId.length > 0 &&
    pathname.startsWith(`${V2_PRODUCTS_PATH}/`)
  const isComponentDetailRoute =
    componentId != null &&
    componentId.length > 0 &&
    pathname.startsWith(`${V2_COMPONENTS_PATH}/`)
  const isSupplierDetailRoute =
    supplierId != null &&
    supplierId.length > 0 &&
    pathname.startsWith(`${V2_SUPPLIERS_PATH}/`)
  const isAssociatedPartyDetailRoute = associatedPartyId != null
  const { data: product, isLoading: isLoadingProduct } = useGetProduct(
    isProductDetailRoute ? productId : ''
  )
  const { data: catalogComponent, isLoading: isLoadingComponent } =
    useGetCatalogComponent(isComponentDetailRoute ? componentId : undefined, {
      enabled: isComponentDetailRoute,
    })
  const { data: catalogSupplier, isLoading: isLoadingSupplier } =
    useGetCatalogSupplier(isSupplierDetailRoute ? supplierId : undefined, {
      enabled: isSupplierDetailRoute,
    })
  const { data: associatedParty, isLoading: isLoadingAssociatedParty } =
    useGetAssociatedParty(
      isAssociatedPartyDetailRoute ? associatedPartyId : undefined
    )

  if (pathname.startsWith(V2_SETTINGS_PATH)) {
    const section = getSettingsSection(pathname)
    const sectionLabelKey = SETTINGS_SECTION_LABEL_KEYS[section]
    const sectionLabel =
      sectionLabelKey != null
        ? t(sectionLabelKey)
        : t('settings.title')

    return {
      items: [
        {
          kind: 'link',
          label: t('nav.items.settings'),
          to: `${V2_SETTINGS_PATH}/account`,
        },
        { kind: 'page', label: sectionLabel },
      ],
      isLoading: false,
    }
  }

  if (pathname === V2_ORGS_ROLES_PATH) {
    return {
      items: [{ kind: 'page', label: t('v2.nav.organisationsAndRoles') }],
      isLoading: false,
    }
  }

  if (isAssociatedPartyDetailRoute && associatedPartyId != null) {
    const partyName =
      associatedParty?.name?.trim() ||
      t('v2.pages.organisationsAndRoles.title')
    return {
      items: [
        {
          kind: 'link',
          label: t('v2.nav.organisationsAndRoles'),
          to: V2_ORGS_ROLES_PATH,
        },
        { kind: 'page', label: partyName },
      ],
      isLoading: isLoadingAssociatedParty && associatedParty == null,
    }
  }

  if (pathname === V2_PRODUCTS_PATH) {
    return {
      items: [{ kind: 'page', label: t('v2.nav.products') }],
      isLoading: false,
    }
  }

  if (pathname === V2_COMPONENTS_PATH) {
    return {
      items: [{ kind: 'page', label: t('v2.nav.components') }],
      isLoading: false,
    }
  }

  if (isComponentDetailRoute && componentId != null) {
    const componentName =
      catalogComponent?.name?.trim() || t('v2.pages.components.fallbackName')
    return {
      items: [
        {
          kind: 'link',
          label: t('v2.nav.components'),
          to: V2_COMPONENTS_PATH,
        },
        { kind: 'page', label: componentName },
      ],
      isLoading: isLoadingComponent && catalogComponent == null,
    }
  }

  if (pathname === V2_SUPPLIERS_PATH) {
    return {
      items: [{ kind: 'page', label: t('v2.nav.suppliers') }],
      isLoading: false,
    }
  }

  if (isSupplierDetailRoute && supplierId != null) {
    const supplierName =
      catalogSupplier?.name?.trim() || t('v2.pages.suppliers.detail.fallbackTitle')
    return {
      items: [
        {
          kind: 'link',
          label: t('v2.nav.suppliers'),
          to: V2_SUPPLIERS_PATH,
        },
        { kind: 'page', label: supplierName },
      ],
      isLoading: isLoadingSupplier && catalogSupplier == null,
    }
  }

  if (isProductDetailRoute && productId != null) {
    const isSourceInspect = pathname.includes('/sources/')
    const isPartsPage = isV2ProductPartsPath(pathname, productId)
    const regulationLaw = getV2ProductLawFromPath(pathname, productId)
    const productName =
      product?.name?.trim() || t('v2.pages.products.fallbackTitle')
    const items: BreadcrumbItemConfig[] = [
      {
        kind: 'link',
        label: t('v2.nav.products'),
        to: V2_PRODUCTS_PATH,
      },
    ]

    if (isSourceInspect) {
      items.push({
        kind: 'link',
        label: productName,
        to: getV2ProductDetailPath(productId),
      })
      items.push({ kind: 'page', label: t('v2.breadcrumb.source') })
    } else if (isPartsPage) {
      items.push({
        kind: 'link',
        label: productName,
        to: getV2ProductDetailPath(productId),
      })
      items.push({ kind: 'page', label: t('v2.breadcrumb.parts') })
    } else if (regulationLaw != null) {
      items.push({
        kind: 'link',
        label: productName,
        to: getV2ProductDetailPath(productId),
      })
      items.push({
        kind: 'page',
        label: t(`v2.applicability.laws.${regulationLaw}`),
      })
    } else {
      items.push({ kind: 'page', label: productName })
    }

    return {
      items,
      isLoading: isLoadingProduct && product == null,
    }
  }

  return {
    items: [{ kind: 'page', label: t('v2.nav.products') }],
    isLoading: false,
  }
}

export function V2Breadcrumbs() {
  const { t } = useTranslation('common')
  const { items, isLoading } = useV2BreadcrumbItems()

  return (
    <Breadcrumb
      className="min-w-0 flex-1"
      aria-label={t('v2.breadcrumb.navLabel')}
    >
      <BreadcrumbList className="flex-nowrap">
        {items.map((item, index) => {
          const isLast = index === items.length - 1

          return (
            <Fragment key={`${item.kind}-${index}`}>
              {index > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem className="min-w-0">
                {item.kind === 'link' ? (
                  <BreadcrumbLink asChild>
                    <Link to={item.to} className="truncate">
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                ) : isLast && isLoading ? (
                  <Skeleton className="h-4 w-28" />
                ) : (
                  <BreadcrumbPage className="max-w-[min(100%,20rem)] truncate">
                    {item.label}
                  </BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
